/**
 * LocodeBadge — render a port reference using the UN/LOCODE standard.
 *
 * UN/LOCODE (UN Code for Trade & Transport Locations) is the maritime
 * industry's universal port identifier — five characters: a 2-letter
 * country code (ISO 3166-1 alpha-2) + a 3-letter location code.
 * Examples: NLRTM = Rotterdam, SGSIN = Singapore, USNYC = New York,
 * INNSA = Nhava Sheva. The AIS DESTINATION field, voyage logs, PSC
 * records, IMO Maritime Single Window submissions and most class
 * society systems all key on UN/LOCODE.
 *
 * Inspectors specifically asked for these to be visible across the
 * product so port references are unambiguous on phone calls / reports.
 *
 * Usage:
 *   <LocodeBadge port="Rotterdam" locode="NLRTM" />
 *   <LocodeBadge locode="SGSIN" />                  // locode-only
 *   <LocodeBadge port="Unknown" />                  // port-only fallback
 */
import React from 'react';

interface Props {
  /** Free-text port name. */
  port?: string | null;
  /** Five-character UN/LOCODE (uppercased automatically). */
  locode?: string | null;
  /** When true, only render if at least one of port/locode is present. */
  hideIfEmpty?: boolean;
  className?: string;
}

const LOCODE_RE = /^[A-Z]{2}[A-Z0-9]{3}$/i;

export default function LocodeBadge({ port, locode, hideIfEmpty, className }: Props) {
  const code = (locode ?? '').trim().toUpperCase();
  const isValid = LOCODE_RE.test(code);
  const portText = (port ?? '').trim();

  if (hideIfEmpty && !isValid && !portText) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${className ?? ''}`}
      title={
        isValid && portText
          ? `${portText} · UN/LOCODE ${code}`
          : isValid
          ? `UN/LOCODE ${code}`
          : portText || 'Unknown port'
      }
    >
      {portText && <span className="text-slate-200">{portText}</span>}
      {isValid && (
        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300">
          {code}
        </span>
      )}
      {!isValid && !portText && <span className="text-slate-500">—</span>}
    </span>
  );
}

/** Heuristic: many AIS DESTINATION strings are filled with a UN/LOCODE.
 *  Spit it out if the string passes the format. Anything else is
 *  free-text the operator typed (e.g. "ROTTERDAM EUROPORT" or "TBA"). */
export function extractLocode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Strip leading prefixes like ">", "TO ", and find the first 5-char
  // alphanumeric token. Conservatively only return when it's exactly
  // one such token so we don't false-match dashboard text.
  const tokens = raw.toUpperCase().split(/[\s>,/-]+/).filter(Boolean);
  for (const t of tokens) {
    if (LOCODE_RE.test(t)) return t;
  }
  return null;
}
